(() => {
  const ROOT_SELECTOR = '[data-rv-predictive-search]';
  const OPTION_SELECTOR = '[data-rv-predictive-option]';
  const DEBOUNCE_MS = 180;
  const REGISTRY_KEY = '__rvPredictiveSearchRegistry';

  if (typeof window.AbortController !== 'function') return;

  const registry = window[REGISTRY_KEY] || {
    instances: new Set(),
    documentListenersReady: false,
    initialise: null,
  };
  window[REGISTRY_KEY] = registry;

  class RvPredictiveSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-rv-predictive-input]');
      this.panel = root.querySelector('[data-rv-predictive-panel]');
      this.status = root.querySelector('[data-rv-predictive-status]');
      this.endpoint = root.dataset.rvPredictiveUrl;
      this.sectionId = root.dataset.rvPredictiveSection;
      this.minimum = Number(root.dataset.rvPredictiveMinCharacters || 2);
      this.timer = 0;
      this.requestSequence = 0;
      this.controller = null;
      this.composing = false;
      this.lifecycleController = new window.AbortController();
      this.ready = false;

      if (!this.input || !this.panel || !this.status || !this.endpoint || !this.sectionId) return;

      const listenerOptions = { signal: this.lifecycleController.signal };

      this.input.addEventListener('input', () => {
        if (!this.composing) this.schedule();
      }, listenerOptions);
      this.input.addEventListener('compositionstart', () => {
        this.composing = true;
      }, listenerOptions);
      this.input.addEventListener('compositionend', () => {
        this.composing = false;
        this.schedule();
      }, listenerOptions);
      this.root.addEventListener('keydown', (event) => this.onKeydown(event), listenerOptions);
      this.root.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.root.contains(document.activeElement)) this.close();
        }, 0);
      }, listenerOptions);
      this.root.addEventListener('submit', () => {
        this.close();
      }, listenerOptions);

      root.dataset.rvPredictiveReady = 'true';
      this.ready = true;
    }

    query() {
      return this.input.value.trim().replace(/\s+/g, ' ');
    }

    announce(message) {
      this.status.textContent = '';
      if (message) window.requestAnimationFrame(() => { this.status.textContent = message; });
    }

    options() {
      return [...this.panel.querySelectorAll(OPTION_SELECTOR)].filter(
        (option) => !option.hasAttribute('hidden') && option.getAttribute('aria-disabled') !== 'true'
      );
    }

    setExpanded(expanded) {
      this.input.setAttribute('aria-expanded', String(expanded));
      this.panel.hidden = !expanded;
    }

    clearSelection() {
      this.options().forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
    }

    activeOption() {
      const activeId = this.input.getAttribute('aria-activedescendant');
      if (!activeId) return null;
      return this.options().find((option) => option.id === activeId) || null;
    }

    selectOption(index) {
      const options = this.options();
      if (!options.length) return;
      const wrappedIndex = (index + options.length) % options.length;
      this.clearSelection();
      options[wrappedIndex].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[wrappedIndex].id);
      options[wrappedIndex].scrollIntoView({ block: 'nearest' });
    }

    cancelRequest() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.requestSequence += 1;
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    close({ focusInput = false, clear = false, cancel = true } = {}) {
      if (cancel) this.cancelRequest();
      this.clearSelection();
      this.panel.removeAttribute('aria-busy');
      this.setExpanded(false);
      if (clear) {
        this.panel.replaceChildren();
        delete this.panel.dataset.rvPredictiveState;
      }
      if (focusInput) this.input.focus();
    }

    showMessage(heading, body, state) {
      const message = document.createElement('div');
      const title = document.createElement('strong');
      const detail = document.createElement('span');
      message.className = 'rv-predictive-search__message';
      message.dataset.rvPredictiveMessage = '';
      message.setAttribute('role', 'option');
      message.setAttribute('aria-disabled', 'true');
      message.setAttribute('aria-selected', 'false');
      title.textContent = heading;
      detail.textContent = body;
      message.append(title, detail);
      this.panel.replaceChildren(message);
      this.panel.dataset.rvPredictiveState = state;
      this.setExpanded(true);
    }

    schedule() {
      const query = this.query();
      this.cancelRequest();
      this.close({ clear: true, cancel: false });

      if (!query) {
        this.announce('');
        return;
      }
      if (query.length < this.minimum) {
        this.announce(`Enter at least ${this.minimum} characters for suggestions.`);
        return;
      }

      const sequence = this.requestSequence;
      this.timer = window.setTimeout(() => this.load(query, sequence), DEBOUNCE_MS);
    }

    buildUrl(query) {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async load(query, sequence) {
      if (sequence !== this.requestSequence || query !== this.query()) return;

      this.controller = new window.AbortController();
      this.panel.setAttribute('aria-busy', 'true');
      this.showMessage('SEARCHING', 'Checking verified pieces.', 'loading');
      this.announce('Loading product suggestions.');

      try {
        const response = await fetch(this.buildUrl(query), {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error('Predictive search request failed.');

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rendered = documentFragment.querySelector('[data-rv-predictive-response]');
        if (!rendered) throw new Error('Predictive search response was not validated.');

        const state = rendered.dataset.rvPredictiveState;
        const count = Number(rendered.dataset.rvPredictiveCount || 0);
        const productCount = rendered.querySelectorAll('[data-rv-predictive-product]').length;
        if (!['results', 'empty'].includes(state) || count !== productCount || (state === 'results') !== (count > 0)) {
          throw new Error('Predictive search response failed its safety contract.');
        }
        if (sequence !== this.requestSequence || query !== this.query()) return;

        const safeNodes = [...rendered.childNodes].map((node) => document.importNode(node, true));
        this.panel.replaceChildren(...safeNodes);
        this.panel.dataset.rvPredictiveState = state;
        this.panel.removeAttribute('aria-busy');
        this.setExpanded(true);

        if (state === 'results') {
          this.announce(`${count} product ${count === 1 ? 'suggestion' : 'suggestions'} available. Use the up and down arrow keys to review them.`);
        } else {
          this.announce('No verified product suggestions. Submit the search to check all safe results.');
        }
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.requestSequence) return;
        this.panel.removeAttribute('aria-busy');
        this.showMessage('SUGGESTIONS UNAVAILABLE', 'Submit the search form to continue.', 'error');
        this.announce('Product suggestions are unavailable. Submit the search form to continue.');
      } finally {
        if (sequence === this.requestSequence) this.controller = null;
      }
    }

    onKeydown(event) {
      if (event.target !== this.input) return;

      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        this.close({ focusInput: true });
        this.announce('Product suggestions closed.');
        return;
      }

      if (event.key === 'Tab' && !this.panel.hidden) {
        this.close();
        return;
      }

      const options = this.options();
      if (!options.length || this.panel.hidden) return;
      const current = this.activeOption();
      const currentIndex = current ? options.indexOf(current) : -1;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (currentIndex === -1) {
          this.selectOption(event.key === 'ArrowDown' ? 0 : options.length - 1);
        } else {
          this.selectOption(currentIndex + (event.key === 'ArrowDown' ? 1 : -1));
        }
        return;
      }

      if (event.key === 'Enter' && current) {
        event.preventDefault();
        this.cancelRequest();
        current.click();
      }
    }

    destroy() {
      this.cancelRequest();
      this.lifecycleController.abort();
      this.root.removeAttribute('data-rv-predictive-ready');
      registry.instances.delete(this);
    }
  }

  function initialise(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.dataset.rvPredictiveReady === 'true') return;
      const instance = new RvPredictiveSearch(root);
      if (instance.ready) registry.instances.add(instance);
    });
  }

  registry.initialise = initialise;
  registry.initialise();

  if (!registry.documentListenersReady) {
    document.addEventListener('pointerdown', (event) => {
      registry.instances.forEach((instance) => {
        if (!instance.root.isConnected) {
          instance.destroy();
        } else if (!instance.root.contains(event.target)) {
          instance.close();
        }
      });
    });
    document.addEventListener('shopify:section:load', (event) => registry.initialise?.(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      registry.instances.forEach((instance) => {
        if (event.target.contains(instance.root)) instance.destroy();
      });
    });
    registry.documentListenersReady = true;
  }
})();
